/**
 * windField.ts — normalized wind-field data & fetch layer for the PPS wind overlay.
 *
 * Fetches multi-coordinate wind grids from Open-Meteo (D1) and normalizes
 * them into the field contract consumed by the sampler (windFieldSampler)
 * and the renderer (C2). Implements the D5 cache / coalescing / cancellation
 * machinery:
 *
 *   - cache keys exactly per contract (TTL 30 min):
 *       wind:grid:{spacing}:{bboxSnapped}:{hourWindow}:sea:best_match
 *       wind:pt:{lat:.2}:{lon:.2}:sea
 *       weather:pt:{lat:.2}:{lon:.2}
 *       swell:pt:{lat:.2}:{lon:.2}
 *   - one in-flight promise per cache key (coalescing)
 *   - epoch counter + AbortController; a resolved response whose epoch is
 *     stale is DISCARDED (never cached, never returned as fresh) — the
 *     epoch check is the authority, AbortError is just an optimization
 *   - 1 retry on network failure (~1 s backoff); no retry on 4xx/5xx
 *   - no requests fired while navigator.onLine === false
 *
 * Grid spacing: nice steps {1.0, 0.5, 0.25, 0.1} ≈ viewport span ÷ 24,
 * bounds expanded 10%, lat clamped ±85, bbox snapped to the spacing grid
 * (so panning reuses cached cells), ≤ ~600 points (coarser fallback steps
 * 2/5/10 deg only when the cap demands it).
 *
 * Units: m/s internally. Direction is meteorological FROM (degrees).
 * u = speed·sin(dir·π/180), v = speed·cos(dir·π/180) per contract D1.
 * Missing cells are NaN in the typed arrays with the `missing` mask set —
 * NEVER 0 (a missing cell must not look calm).
 */

// ── Contract types ──

import { openMeteoFetch } from './openMeteo';
import {
  dedupeCells,
  forecastCellsCacheKey,
  planCellsForSampling,
  planWindFieldCells,
  type ForecastCell,
  type ForecastCellSampling,
  type ForecastDataType,
  type ForecastSource,
} from './forecastCells';
// forecastCache is imported LAZILY inside resolveForecastCells: it pulls in
// db.ts → authStore → pocketbase, which touches `window` at module scope.
// Node-env test files that import windField (or forecastCells, which re-
// exports windField values) must not load that chain until a fetch actually
// runs — and the browser always has `window`, so the lazy import is free.
import type { ForecastCacheRecord } from './forecastCache';
import {
  blockRemainingMs,
  clearExpiredBlock,
  computeBlockedUntil,
  hostForUrl,
  parseRetryAfterSeconds,
  recordBlock,
} from './forecastBackoff';

export interface WindGridBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface WindGrid {
  bounds: WindGridBounds;
  spacingDeg: number;
  spacingKm: number;
  cols: number;
  rows: number;
  cellSelection: 'sea';
}

/** Per-hour component arrays (m/s; direction in deg FROM). NaN = missing. */
export interface WindLevels {
  u: Float32Array[];
  v: Float32Array[];
  speed: Float32Array[];
  direction: Float32Array[];
}

/**
 * Per-node cell arrays. `u/v/speed/direction` are hour-major flat:
 * index = h*(cols*rows) + node (levels are subarray views over them).
 * `missing` is per-node (1 = no data for any hour); NaN fills missing
 * cells — never 0.
 */
export interface WindCells {
  lon: Float32Array;
  lat: Float32Array;
  u: Float32Array;
  v: Float32Array;
  speed: Float32Array;
  direction: Float32Array;
  missing: Uint8Array;
}

/** Normalized wind grid field (the map overlay contract). */
export interface WindField {
  /** ISO hour of the first forecast step (window anchor). */
  timestamp: string;
  /** Epoch ms when the response was normalized. */
  fetchedAt: number;
  model: 'best_match';
  source: 'open-meteo';
  grid: WindGrid;
  /** ISO hour labels, one per hourly step (72 for forecast_days=3). */
  hours: string[];
  levels: WindLevels;
  cells: WindCells;
}

/** One hourly step of a point forecast (null = missing, never 0). */
export interface WindPointHour {
  time: string;
  u: number | null;
  v: number | null;
  speed: number | null; // m/s
  direction: number | null; // deg meteorological FROM
  gust?: number | null; // m/s (wind + weather point calls)
  temperature?: number | null; // °C (weather point call)
  apparentTemperature?: number | null; // °C
  precipitationProbability?: number | null; // %
  weatherCode?: number | null; // WMO weather code (weather calls, C1)
  humidity?: number | null; // relative humidity % (weather calls, C1)
  precipitation?: number | null; // rain amount mm (weather calls, C1)
  cloudCover?: number | null; // cloud cover % (weather calls)
  pressureMsl?: number | null; // hPa (pressure grid)
  swellHeight?: number | null; // m (swell point call)
  swellDirection?: number | null; // deg FROM
  swellPeriod?: number | null; // s
}

/** Normalized point forecast (the Conditions-surface contract). */
export interface WindFieldPointForecast {
  lat: number;
  lon: number;
  hours: WindPointHour[];
}

export interface HourWindow {
  /** First requested hour offset (0 = the fetch's current hour). */
  start: number;
  /** End hour offset (exclusive). */
  end: number;
}

// ── Geometry constants ──

export const GRID_NICE_STEPS = [1.0, 0.5, 0.25, 0.1];
/** Coarser fallbacks used ONLY when the ~600-pt cap cannot be met by a nice step. */
export const GRID_COARSEN_STEPS = [2.0, 5.0, 10.0];
export const MAX_GRID_POINTS = 600;
export const TARGET_SPAN_CELLS = 24;
export const LAT_CLAMP = 85;
export const DEG_TO_KM = 111.32;
export const FORECAST_HOURS = 72; // forecast_days=3
/**
 * Days of hourly point forecast behind the Conditions tables (wind,
 * weather, swell at the forecast point). A week is about as far as the
 * global models and the marine wave models keep useful skill; beyond it the
 * day cards show tide only. Map overlays stay on FORECAST_HOURS.
 */
export const POINT_FORECAST_DAYS = 7;

// ── Fetch machinery constants ──

const CACHE_TTL_MS = 30 * 60 * 1000;
const RETRY_DELAY_MS = 1000;
const GRID_URL_BASE = 'https://api.open-meteo.com/v1/forecast';
const SWELL_URL_BASE = 'https://marine-api.open-meteo.com/v1/marine';

// ── Errors ──

export class WindHttpError extends Error {
  status: number;
  constructor(status: number) {
    super(`Open-Meteo HTTP ${status}`);
    this.status = status;
    this.name = 'WindHttpError';
  }
}

/**
 * The provider answered 429 (Too Many Requests). A per-host pause was
 * recorded for this host before the error was raised.
 */
export class WindLimitError extends WindHttpError {
  reason?: string;
  retryAfterSeconds: number | null;
  constructor(status: number, reason?: string, retryAfterSeconds: number | null = null) {
    super(status);
    this.name = 'WindLimitError';
    this.reason = reason;
    this.retryAfterSeconds = retryAfterSeconds;
    this.message = reason
      ? `Open-Meteo HTTP ${status}: ${reason}`
      : `Open-Meteo HTTP ${status} (rate limit)`;
  }
}

/** A per-host backoff is active — the request was refused without a network call. */
export class WindBlockedError extends Error {
  host: string;
  remainingMs: number;
  constructor(host: string, remainingMs: number) {
    super(`forecast host ${host} is rate-limited; blocked for ${Math.ceil(remainingMs / 1000)}s`);
    this.name = 'WindBlockedError';
    this.host = host;
    this.remainingMs = remainingMs;
  }
}

export class WindOfflineError extends Error {
  constructor() {
    super('offline — not firing forecast requests');
    this.name = 'WindOfflineError';
  }
}

// ── Grid geometry (spacing selection + bbox snapping) ──

export interface GridGeometry {
  bounds: WindGridBounds;
  spacingDeg: number;
  spacingKm: number;
  cols: number;
  rows: number;
}

/**
 * Choose grid spacing + snapped bounds for a viewport.
 *
 * 1. Expand bounds by 10% each side.
 * 2. Clamp latitude to ±85.
 * 3. spacing = largest nice step ≤ (larger span ÷ 24).
 * 4. If that exceeds the point cap, coarsen (nice steps first, then
 *    2/5/10° fallbacks) until the cap holds. The default cap is the
 *    ~600-pt field grid; opt-in layers pass their own caps (wind 169,
 *    pressure 81 — v56 Task 11).
 * 5. Snap the bbox to multiples of the spacing so panning reuses cells.
 */
export function computeGridGeometry(
  bounds: WindGridBounds,
  maxPoints: number = MAX_GRID_POINTS,
): GridGeometry {
  const lonSpan = bounds.east - bounds.west;
  const latSpan = bounds.north - bounds.south;
  const padX = lonSpan * 0.1;
  const padY = latSpan * 0.1;

  const rawWest = bounds.west - padX;
  const rawEast = bounds.east + padX;
  const rawSouth = Math.max(-LAT_CLAMP, bounds.south - padY);
  const rawNorth = Math.min(LAT_CLAMP, bounds.north + padY);

  const span = Math.max(rawEast - rawWest, rawNorth - rawSouth);
  const target = span / TARGET_SPAN_CELLS;

  let spacing = GRID_NICE_STEPS[GRID_NICE_STEPS.length - 1]; // 0.1 floor
  for (const s of GRID_NICE_STEPS) {
    if (s <= target) {
      spacing = s;
      break;
    }
  }

  // Coarsen until the point cap holds (finest candidate that fits).
  const candidates = [...GRID_NICE_STEPS].reverse(); // ascending
  for (const s of GRID_COARSEN_STEPS) candidates.push(s);
  for (const s of candidates) {
    if (s < spacing) continue;
    const pts =
      (Math.ceil((rawEast - rawWest) / s) + 1) * (Math.ceil((rawNorth - rawSouth) / s) + 1);
    if (pts <= maxPoints) {
      spacing = s;
      break;
    }
  }

  // Snap the bbox to the spacing grid (floor west/south, ceil east/north
  // so the snapped grid always covers the viewport; normalized to kill
  // float dust). Panning reuses cells because the bbox is snapped.
  const snapLo = (v: number) => Math.floor(v / spacing) * spacing;
  const snapHi = (v: number) => Math.ceil(v / spacing) * spacing;
  const west = snapLo(rawWest);
  const east = snapHi(rawEast);
  const south = snapLo(rawSouth);
  const north = snapHi(rawNorth);
  const snapped: WindGridBounds = {
    west: parseFloat(west.toFixed(2)),
    south: parseFloat(south.toFixed(2)),
    east: parseFloat(east.toFixed(2)),
    north: parseFloat(north.toFixed(2)),
  };

  const cols = Math.round((snapped.east - snapped.west) / spacing) + 1;
  const rows = Math.round((snapped.north - snapped.south) / spacing) + 1;

  return {
    bounds: snapped,
    spacingDeg: spacing,
    spacingKm: spacing * DEG_TO_KM,
    cols,
    rows,
  };
}

/** Node longitude at column i (row-major node index = j*cols + i). */
export function nodeLon(geom: GridGeometry, i: number): number {
  return parseFloat((geom.bounds.west + i * geom.spacingDeg).toFixed(2));
}

/** Node latitude at row j. */
export function nodeLat(geom: GridGeometry, j: number): number {
  return parseFloat((geom.bounds.south + j * geom.spacingDeg).toFixed(2));
}

// ── Cache keys (contract D5, exact) ──

export function windPointCacheKey(lat: number, lon: number): string {
  return `wind:pt:${lat.toFixed(2)}:${lon.toFixed(2)}:sea`;
}

export function weatherPointCacheKey(lat: number, lon: number): string {
  return `weather:pt:${lat.toFixed(2)}:${lon.toFixed(2)}`;
}

export function swellPointCacheKey(lat: number, lon: number): string {
  return `swell:pt:${lat.toFixed(2)}:${lon.toFixed(2)}`;
}

// ── URL builders (D1) ──

function coordsParam(geom: GridGeometry): { lats: string[]; lons: string[] } {
  const lats: string[] = [];
  const lons: string[] = [];
  for (let j = 0; j < geom.rows; j++) {
    const lat = nodeLat(geom, j);
    for (let i = 0; i < geom.cols; i++) {
      lats.push(lat.toFixed(2));
      lons.push(nodeLon(geom, i).toFixed(2));
    }
  }
  return { lats, lons };
}

export function buildGridUrl(geom: GridGeometry, hourWindow: HourWindow): string {
  const { lats, lons } = coordsParam(geom);
  const params = new URLSearchParams({
    latitude: lats.join(','),
    longitude: lons.join(','),
    hourly: 'wind_speed_10m,wind_direction_10m,wind_gusts_10m',
    wind_speed_unit: 'ms',
    cell_selection: 'sea',
    timezone: 'UTC',
    forecast_days: String(Math.max(1, Math.ceil((hourWindow.end - hourWindow.start) / 24))),
  });
  return `${GRID_URL_BASE}?${params.toString()}`;
}

export function buildWindPointUrl(lat: number, lon: number): string {
  const params = new URLSearchParams({
    latitude: lat.toFixed(2),
    longitude: lon.toFixed(2),
    hourly: 'wind_speed_10m,wind_direction_10m,wind_gusts_10m',
    wind_speed_unit: 'ms',
    cell_selection: 'sea',
    timezone: 'UTC',
    forecast_days: String(POINT_FORECAST_DAYS),
  });
  return `${GRID_URL_BASE}?${params.toString()}`;
}

export function buildWeatherPointUrl(lat: number, lon: number): string {
  const params = new URLSearchParams({
    latitude: lat.toFixed(2),
    longitude: lon.toFixed(2),
    hourly:
      'wind_speed_10m,wind_direction_10m,wind_gusts_10m,temperature_2m,apparent_temperature,precipitation_probability,weather_code,relative_humidity_2m,precipitation,cloud_cover,pressure_msl',
    wind_speed_unit: 'ms',
    cell_selection: 'nearest',
    timezone: 'UTC',
    forecast_days: String(POINT_FORECAST_DAYS),
  });
  return `${GRID_URL_BASE}?${params.toString()}`;
}

export function buildSwellPointUrl(lat: number, lon: number): string {
  const params = new URLSearchParams({
    latitude: lat.toFixed(2),
    longitude: lon.toFixed(2),
    hourly: 'swell_wave_height,swell_wave_direction,swell_wave_period',
    timezone: 'UTC',
    forecast_days: String(POINT_FORECAST_DAYS),
  });
  return `${SWELL_URL_BASE}?${params.toString()}`;
}

// ── u/v conversion (contract D1: direction is meteorological FROM) ──

export function uvFromSpeedDir(speedMs: number, dirDeg: number): { u: number; v: number } {
  const rad = (dirDeg * Math.PI) / 180;
  return { u: speedMs * Math.sin(rad), v: speedMs * Math.cos(rad) };
}

// ── Cache / coalescing / epoch machinery ──

interface CacheEntry {
  ts: number;
  value: unknown;
}

interface InflightEntry {
  epoch: number;
  promise: Promise<unknown>;
}

const _cache = new Map<string, CacheEntry>();
const _inflight = new Map<string, InflightEntry>();

let _gridEpoch = 0;
const _gridControllers = new Set<AbortController>();

/** Per-key controllers so a cache reset can abort in-flight cell fetches. */
const _cellControllers = new Map<string, AbortController>();

/**
 * Per-cell memory cache (v56 Task 3): key = forecastCellCacheKey(...),
 * record = the same shape the persistent store uses, so memory and
 * persistent records share one freshness classification. Each fetched
 * cell is stored separately — an overlapping viewport reuses the cells
 * it has and requests only the ones it lacks.
 */
const _cellCache = new Map<string, ForecastCacheRecord>();

/** Clear all cached fields + in-flight state. Test support / logout hygiene. */
export function resetWindFieldCache(): void {
  _cache.clear();
  _inflight.clear();
  _gridEpoch = 0;
  for (const c of _gridControllers) c.abort();
  _gridControllers.clear();
  // Per-kind forecast-cell requests (B1) — same reset semantics. Cells
  // never supersede each other across keys, so each key owns one controller.
  for (const c of _cellControllers.values()) c.abort();
  _cellControllers.clear();
  _cellCache.clear();
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException
    ? e.name === 'AbortError'
    : (e as { name?: string } | null)?.name === 'AbortError';
}

function abortError(): DOMException {
  return new DOMException('aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          reject(abortError());
        },
        { once: true },
      );
    }
  });
}

/**
 * Fetch + JSON-decode with D5 semantics: offline guard, per-host rate-limit
 * backoff (v56 Task 8), 1 retry on network failure (~1 s backoff), no retry
 * on 4xx/5xx, abort passthrough.
 */
async function fetchJson(url: string, signal?: AbortSignal): Promise<unknown> {
  // A controller aborted while we were parked (lazy cache import / cache
  // read) must never fire a request: the response could not be served as
  // fresh anyway, and real fetch() would reject an aborted signal.
  if (signal?.aborted) throw abortError();
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new WindOfflineError();
  }
  // While a provider pause is active, never touch the network: callers
  // serve saved same-cell fallback or surface a clear blocked error.
  const host = hostForUrl(url);
  const remaining = blockRemainingMs(host);
  if (remaining > 0) throw new WindBlockedError(host, remaining);

  let attempt = 0;
  for (;;) {
    try {
      const res = await openMeteoFetch(url, { signal });
      if (!res.ok) {
        if (res.status === 429) {
          // Provider limit: persist the pause for THIS host (Retry-After
          // wins, otherwise the reason phrase decides the duration).
          const retryAfterValue = res.headers?.get ? res.headers.get('retry-after') : null;
          const retryAfterSeconds = parseRetryAfterSeconds(retryAfterValue);
          let reason: string | undefined;
          try {
            const body = (await res.json()) as { reason?: unknown };
            if (typeof body?.reason === 'string') reason = body.reason;
          } catch {
            // Non-JSON error body — the pause falls back to the unknown rule.
          }
          recordBlock(host, computeBlockedUntil(Date.now(), { retryAfterSeconds, reason }));
          throw new WindLimitError(res.status, reason, retryAfterSeconds);
        }
        throw new WindHttpError(res.status);
      }
      // HTTP success proves the host is reachable — drop expired block state.
      clearExpiredBlock(host);
      return (await res.json()) as unknown;
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) throw abortError();
      if (e instanceof WindHttpError) throw e; // no retry on 4xx/5xx (incl. limits)
      if (attempt >= 1) throw e;
      attempt++;
      await sleep(RETRY_DELAY_MS, signal);
    }
  }
}

// ── Response normalization ──

interface OpenMeteoHourly {
  time?: string[];
  wind_speed_10m?: (number | null)[];
  wind_direction_10m?: (number | null)[];
  wind_gusts_10m?: (number | null)[];
  temperature_2m?: (number | null)[];
  apparent_temperature?: (number | null)[];
  precipitation_probability?: (number | null)[];
  weather_code?: (number | null)[];
  relative_humidity_2m?: (number | null)[];
  precipitation?: (number | null)[];
  cloud_cover?: (number | null)[];
  pressure_msl?: (number | null)[];
  swell_wave_height?: (number | null)[];
  swell_wave_direction?: (number | null)[];
  swell_wave_period?: (number | null)[];
}

interface OpenMeteoEntry {
  latitude?: number;
  longitude?: number;
  hourly?: OpenMeteoHourly;
}

const isoHour = (t: string): string => (t.endsWith('Z') ? t : `${t}Z`);

/** Convert an Open-Meteo grid response (array, one entry per coordinate) into a WindField. */
export function normalizeGridResponse(
  data: unknown,
  geom: GridGeometry,
  hourWindow: HourWindow,
): WindField {
  const entries = (Array.isArray(data) ? data : []) as OpenMeteoEntry[];
  const n = geom.cols * geom.rows;
  const hoursRaw = entries[0]?.hourly?.time;
  if (!hoursRaw || hoursRaw.length === 0) {
    throw new Error('open-meteo grid response has no hourly time array');
  }
  const hours = hoursRaw.map(isoHour);
  const hourCount = Math.min(hours.length, hourWindow.end - hourWindow.start);

  const lon = new Float32Array(n);
  const lat = new Float32Array(n);
  const missing = new Uint8Array(n);
  const u = new Float32Array(hourCount * n).fill(NaN);
  const v = new Float32Array(hourCount * n).fill(NaN);
  const speed = new Float32Array(hourCount * n).fill(NaN);
  const direction = new Float32Array(hourCount * n).fill(NaN);

  for (let j = 0; j < geom.rows; j++) {
    for (let i = 0; i < geom.cols; i++) {
      const idx = j * geom.cols + i;
      lon[idx] = nodeLon(geom, i);
      lat[idx] = nodeLat(geom, j);
      const entry = entries[idx];
      const hourly = entry?.hourly;
      if (!hourly || !hourly.time) {
        missing[idx] = 1;
        continue;
      }
      const speeds = hourly.wind_speed_10m;
      const dirs = hourly.wind_direction_10m;
      if (!speeds || !dirs) {
        missing[idx] = 1;
        continue;
      }
      let anyNull = false;
      for (let h = 0; h < hourCount; h++) {
        const s = speeds[h];
        const d = dirs[h];
        if (s == null || d == null) {
          anyNull = true;
          continue;
        }
        const { u: uu, v: vv } = uvFromSpeedDir(s, d);
        u[h * n + idx] = uu;
        v[h * n + idx] = vv;
        speed[h * n + idx] = s;
        direction[h * n + idx] = d;
      }
      if (anyNull) missing[idx] = 1;
    }
  }

  const levelViews = (arr: Float32Array): Float32Array[] =>
    Array.from({ length: hourCount }, (_, h) => arr.subarray(h * n, (h + 1) * n));

  return {
    timestamp: hours[0],
    fetchedAt: Date.now(),
    model: 'best_match',
    source: 'open-meteo',
    grid: { ...geom, cellSelection: 'sea' },
    hours: hours.slice(0, hourCount),
    levels: {
      u: levelViews(u),
      v: levelViews(v),
      speed: levelViews(speed),
      direction: levelViews(direction),
    },
    cells: { lon, lat, u, v, speed, direction, missing },
  };
}

/** Normalize a single-coordinate point response into WindFieldPointForecast. */
export function normalizePointResponse(
  data: unknown,
  lat: number,
  lon: number,
): WindFieldPointForecast {
  const entry = (Array.isArray(data) ? data[0] : data) as OpenMeteoEntry | undefined;
  const hourly = entry?.hourly;
  const times = hourly?.time ?? [];
  const hours: WindPointHour[] = times.map((t, i) => {
    const s = hourly?.wind_speed_10m?.[i] ?? null;
    const d = hourly?.wind_direction_10m?.[i] ?? null;
    const uv = s != null && d != null ? uvFromSpeedDir(s, d) : null;
    return {
      time: isoHour(t),
      u: uv?.u ?? null,
      v: uv?.v ?? null,
      speed: s,
      direction: d,
      gust: hourly?.wind_gusts_10m?.[i] ?? null,
      temperature: hourly?.temperature_2m?.[i] ?? null,
      apparentTemperature: hourly?.apparent_temperature?.[i] ?? null,
      precipitationProbability: hourly?.precipitation_probability?.[i] ?? null,
      weatherCode: hourly?.weather_code?.[i] ?? null,
      humidity: hourly?.relative_humidity_2m?.[i] ?? null,
      precipitation: hourly?.precipitation?.[i] ?? null,
      cloudCover: hourly?.cloud_cover?.[i] ?? null,
      pressureMsl: hourly?.pressure_msl?.[i] ?? null,
      swellHeight: hourly?.swell_wave_height?.[i] ?? null,
      swellDirection: hourly?.swell_wave_direction?.[i] ?? null,
      swellPeriod: hourly?.swell_wave_period?.[i] ?? null,
    };
  });
  return { lat: entry?.latitude ?? lat, lon: entry?.longitude ?? lon, hours };
}

/**
 * Build a WindField from per-cell point forecasts (v56 Task 11).
 *
 * The shared cells path (resolveForecastCells) returns one point forecast
 * per planned node; this assembles the field contract the renderers need,
 * with the SAME missing semantics as normalizeGridResponse: a node whose
 * point has no hours, or null speed/direction in a hour, is NaN + missing
 * (never 0). Node coordinates always come from the geometry, so the field
 * stays aligned with the planned grid regardless of returned coords.
 */
export function windFieldFromCellPoints(
  points: WindFieldPointForecast[],
  geom: GridGeometry,
  hourWindow: HourWindow,
): WindField {
  const n = geom.cols * geom.rows;
  const hoursRaw = points[0]?.hours.map((h) => h.time) ?? [];
  if (hoursRaw.length === 0) {
    throw new Error('wind field cells carry no hourly time array');
  }
  const hours = hoursRaw.map(isoHour);
  const hourCount = Math.min(hours.length, hourWindow.end - hourWindow.start);

  const lon = new Float32Array(n);
  const lat = new Float32Array(n);
  const missing = new Uint8Array(n);
  const u = new Float32Array(hourCount * n).fill(NaN);
  const v = new Float32Array(hourCount * n).fill(NaN);
  const speed = new Float32Array(hourCount * n).fill(NaN);
  const direction = new Float32Array(hourCount * n).fill(NaN);

  for (let j = 0; j < geom.rows; j++) {
    for (let i = 0; i < geom.cols; i++) {
      const idx = j * geom.cols + i;
      lon[idx] = nodeLon(geom, i);
      lat[idx] = nodeLat(geom, j);
      const point = points[idx];
      if (!point || point.hours.length === 0) {
        missing[idx] = 1;
        continue;
      }
      let anyNull = false;
      for (let h = 0; h < hourCount; h++) {
        const s = point.hours[h]?.speed ?? null;
        const d = point.hours[h]?.direction ?? null;
        if (s == null || d == null) {
          anyNull = true;
          continue;
        }
        const { u: uu, v: vv } = uvFromSpeedDir(s, d);
        u[h * n + idx] = uu;
        v[h * n + idx] = vv;
        speed[h * n + idx] = s;
        direction[h * n + idx] = d;
      }
      if (anyNull) missing[idx] = 1;
    }
  }

  const levelViews = (arr: Float32Array): Float32Array[] =>
    Array.from({ length: hourCount }, (_, h) => arr.subarray(h * n, (h + 1) * n));

  return {
    timestamp: hours[0],
    fetchedAt: Date.now(),
    model: 'best_match',
    source: 'open-meteo',
    grid: { ...geom, cellSelection: 'sea' },
    hours: hours.slice(0, hourCount),
    levels: {
      u: levelViews(u),
      v: levelViews(v),
      speed: levelViews(speed),
      direction: levelViews(direction),
    },
    cells: { lon, lat, u, v, speed, direction, missing },
  };
}

// ── Public API ──

export interface WindGridRequest {
  bounds: WindGridBounds;
  hourWindow?: HourWindow;
}

export interface WindGridResult {
  field: WindField | null; // null when the response was superseded (stale)
  stale: boolean;
  fromCache: boolean;
}

/**
 * Fetch (or serve from cache) the shared opt-in wind grid covering `bounds`.
 *
 * v56 Task 11: the wind field rides the SAME per-cell cache + per-host
 * backoff as weather/pressure. It plans through planWindFieldCells (capped
 * at 169 cells), keys through forecastCellsCacheKey(kind 'wind'), and
 * rebuilds the field from per-cell records — so Wind particles, Wind colour
 * and Wind barbs share one request and one cache, and a weather-host block
 * stops the field too (marine stays independent).
 *
 * Grid supersede semantics are preserved: a new grid request aborts previous
 * in-flight grid fetches, and any response resolving with an old epoch is
 * discarded as a FIELD (its per-cell records still land in the shared cache
 * — they are valid data). Concurrent requests with the same cache key
 * coalesce onto one in-flight promise.
 */
export async function fetchWindGrid(request: WindGridRequest): Promise<WindGridResult> {
  const plan = planWindFieldCells(request.bounds);
  const geom = plan.geometry;
  const hourWindow: HourWindow = request.hourWindow ?? { start: 0, end: FORECAST_HOURS };
  const key = forecastCellsCacheKey('wind', geom, hourWindow);

  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return { field: cached.value as WindField, stale: false, fromCache: true };
  }

  const inflight = _inflight.get(key);
  if (inflight && inflight.epoch === _gridEpoch) {
    return inflight.promise as Promise<WindGridResult>;
  }
  if (inflight) _inflight.delete(key); // stale entry — replace with a fresh fetch

  // Supersede every previous grid fetch (viewport authority pattern).
  _gridEpoch++;
  const epoch = _gridEpoch;
  for (const c of _gridControllers) c.abort();
  _gridControllers.clear();

  const controller = new AbortController();
  _gridControllers.add(controller);

  const uniqueCells = dedupeCells(plan.cells);
  const dayAnchor = forecastDayAnchor(hourWindow);
  const promise = (async (): Promise<WindGridResult> => {
    try {
      const resolved = await resolveForecastCells(
        'wind',
        uniqueCells,
        hourWindow,
        dayAnchor,
        controller.signal,
      );
      const field = windFieldFromCellPoints(resolved.points, geom, hourWindow);
      if (epoch !== _gridEpoch) {
        // Superseded while in flight — the FIELD is discarded (never
        // served as fresh), but the per-cell records were already saved
        // to the shared cache: valid data, reusable by any caller.
        return { field: null, stale: true, fromCache: false };
      }
      _cache.set(key, { ts: Date.now(), value: field });
      return { field, stale: false, fromCache: resolved.fromCache };
    } catch (e) {
      if (isAbortError(e) || epoch !== _gridEpoch) {
        return { field: null, stale: true, fromCache: false };
      }
      throw e;
    } finally {
      // Only drop our own entry — a newer fetch for the same key may have
      // replaced us while we were in flight (it carries a newer epoch).
      const cur = _inflight.get(key);
      if (cur && cur.epoch === epoch) _inflight.delete(key);
      _gridControllers.delete(controller);
    }
  })();

  _inflight.set(key, { epoch, promise });
  return promise;
}

/**
 * Fetch a single-coordinate point forecast with the same cache/coalescing
 * semantics (per-key, no supersede). `build` maps the normalized response
 * into the point forecast.
 */
async function fetchPoint(
  key: string,
  url: string,
  lat: number,
  lon: number,
): Promise<WindFieldPointForecast> {
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.value as WindFieldPointForecast;
  }
  const inflight = _inflight.get(key);
  if (inflight) return inflight.promise as Promise<WindFieldPointForecast>;

  const promise = (async (): Promise<WindFieldPointForecast> => {
    try {
      const data = await fetchJson(url);
      const forecast = normalizePointResponse(data, lat, lon);
      _cache.set(key, { ts: Date.now(), value: forecast });
      return forecast;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, { epoch: _gridEpoch, promise });
  return promise;
}

/** Point wind (speed/direction/gusts) at a single coordinate. */
export function fetchWindPoint(lat: number, lon: number): Promise<WindFieldPointForecast> {
  return fetchPoint(windPointCacheKey(lat, lon), buildWindPointUrl(lat, lon), lat, lon);
}

/** Point weather (wind + temperature + precip probability). */
export function fetchWeatherPoint(lat: number, lon: number): Promise<WindFieldPointForecast> {
  return fetchPoint(weatherPointCacheKey(lat, lon), buildWeatherPointUrl(lat, lon), lat, lon);
}

/** Point swell (wave height/direction/period) via the marine API. */
export function fetchSwellPoint(lat: number, lon: number): Promise<WindFieldPointForecast> {
  return fetchPoint(swellPointCacheKey(lat, lon), buildSwellPointUrl(lat, lon), lat, lon);
}

// ── Viewport cell-set requests (B1-2/B1-3, v56 Task 3) ──
// The shared forecast foundation for the weather-point / swell-point /
// pressure layers AND the Conditions viewport surface: a viewport → a
// bounded set of REAL forecast cells, deduplicated, assembled from a
// per-cell MEMORY cache, the device-wide PERSISTENT cache and (only for
// the cells neither can serve fresh) ONE multi-location network request.
// In-flight requests coalesce per cache key. Unlike the wind grid there
// is NO supersede: the Conditions store and the marker layers all fetch
// the same kinds for the same viewport, so a newer request must never
// abort data another caller is waiting on (each key's one in-flight fetch
// is shared, not cancelled).

export interface ForecastCellsRequest {
  bounds: WindGridBounds;
  kind: ForecastDataType;
  hourWindow?: HourWindow;
  /** Selected forecast hour ISO from the shared clock (stamped on points). */
  forecastTime?: string;
  /**
   * Cell sampling mode: 'viewport-summary' (the fixed 25-cell Conditions
   * budget) or 'field' (the full snapped grid). Defaults to 'field' so the
   * existing marker-layer renderers keep their current grids; Conditions
   * opts into the summary explicitly.
   */
  sampling?: ForecastCellSampling;
  /**
   * Optional field cap for 'field' sampling (v56 Task 11): pressure passes
   * MAX_PRESSURE_FIELD_CELLS (81). When omitted the full ~600-pt grid is
   * used. The viewport-summary mode ignores this — the 25-cell Conditions
   * cap is never raised.
   */
  maxPoints?: number;
}

export interface ForecastCellsResult {
  /** Deduplicated real cell centres (aligned with `points` 1:1). */
  cells: ForecastCell[];
  /** Per-cell forecasts, same order as `cells`. */
  points: WindFieldPointForecast[];
  /** ISO hour the model run started (first response hour; '' on abort). */
  sourceTime: string;
  /** Selected forecast hour (request value, else the run start). */
  forecastTime: string;
  source: ForecastSource;
  kind: ForecastDataType;
  stale: boolean;
  fromCache: boolean;
  /**
   * Age of the oldest saved record used to serve this result (0 for fully
   * fresh data). Non-zero only when `fallback` is true — the source for
   * the "Saved forecast, updated X ago" label. Optional so existing test
   * fixtures (and consumers outside this refactor) still type-check; the
   * implementation always sets it.
   */
  cacheAgeMs?: number;
  /** True when any cell came from a stale-but-usable saved record. */
  fallback?: boolean;
  /** How many cells this viewport requested (the planned, deduped set). */
  requestedCellCount?: number;
  /** How many of those cells came from the network in this call (0 = none). */
  networkCellCount?: number;
}

/** Source label per data kind ('open-meteo-marine' only for swell). */
export function forecastSourceFor(kind: ForecastDataType): ForecastSource {
  return kind === 'swell' ? 'open-meteo-marine' : 'open-meteo';
}

/** Land/sea cell selection per kind (mirrors the provider URL contract). */
export function forecastCellSelectionFor(kind: ForecastDataType): 'nearest' | 'sea' {
  return kind === 'weather' ? 'nearest' : 'sea';
}

/**
 * UTC YYYYMMDD anchor of the first requested forecast hour — the day
 * component of the persistent per-cell cache keys. A request whose window
 * starts on a different UTC day never reuses the previous day's records.
 */
export function forecastDayAnchor(hourWindow: HourWindow, now: number = Date.now()): number {
  const first = new Date(now + hourWindow.start * 3_600_000);
  return first.getUTCFullYear() * 10_000 + (first.getUTCMonth() + 1) * 100 + first.getUTCDate();
}

/** Shared core of the multi-coordinate forecast URL builders. */
function buildForecastCellsUrlCore(
  kind: ForecastDataType,
  lats: string[],
  lons: string[],
  hourWindow: HourWindow,
): string {
  const base = kind === 'swell' ? SWELL_URL_BASE : GRID_URL_BASE;
  const hourly =
    kind === 'swell'
      ? 'swell_wave_height,swell_wave_direction,swell_wave_period'
      : kind === 'pressure'
        ? 'pressure_msl'
        : 'wind_speed_10m,wind_direction_10m,wind_gusts_10m,temperature_2m,apparent_temperature,precipitation_probability,weather_code,relative_humidity_2m,precipitation,cloud_cover,pressure_msl';
  const params = new URLSearchParams({
    latitude: lats.join(','),
    longitude: lons.join(','),
    hourly,
    wind_speed_unit: 'ms',
    cell_selection: forecastCellSelectionFor(kind),
    timezone: 'UTC',
    forecast_days: String(Math.max(1, Math.ceil((hourWindow.end - hourWindow.start) / 24))),
  });
  return `${base}?${params.toString()}`;
}

/**
 * Build the multi-coordinate Open-Meteo URL for a bounded cell set.
 * Swell goes to the marine API; weather/pressure to the forecast API.
 */
export function buildForecastCellsUrl(
  kind: ForecastDataType,
  geom: GridGeometry,
  hourWindow: HourWindow,
): string {
  const { lats, lons } = coordsParam(geom);
  return buildForecastCellsUrlCore(kind, lats, lons, hourWindow);
}

/**
 * Build the URL for an EXPLICIT cell list — the missing/expired subset of
 * a viewport. One multi-location request carries only the cells no cache
 * could serve, so overlapping viewports fetch far fewer coordinates.
 */
export function buildForecastCellsUrlForCells(
  kind: ForecastDataType,
  cells: ForecastCell[],
  hourWindow: HourWindow,
): string {
  return buildForecastCellsUrlCore(
    kind,
    cells.map((c) => c.lat.toFixed(2)),
    cells.map((c) => c.lon.toFixed(2)),
    hourWindow,
  );
}

/**
 * Normalize one grid entry into a point forecast. By default the point
 * is pinned to the PLANNED cell coordinates (response lat/lon may be
 * snapped differently — the plan's cells are the contract, so `cells`
 * and `points` stay aligned). For SWELL requests the pinning is
 * released: `cell_selection=sea` can resolve a coastal LAND point to a
 * DIFFERENT offshore marine cell, and the point must carry the cell the
 * marine API actually used — never sea-selected swell at the original
 * land coordinate. (Weather/pressure keep the shared planned grid.)
 */
function normalizeCellForecast(
  entry: OpenMeteoEntry | undefined,
  cell: ForecastCell,
  keepReturnedCoords = false,
): WindFieldPointForecast {
  const hourly = entry?.hourly;
  const times = hourly?.time ?? [];
  const hours: WindPointHour[] = times.map((t, i) => {
    const s = hourly?.wind_speed_10m?.[i] ?? null;
    const d = hourly?.wind_direction_10m?.[i] ?? null;
    const uv = s != null && d != null ? uvFromSpeedDir(s, d) : null;
    return {
      time: isoHour(t),
      u: uv?.u ?? null,
      v: uv?.v ?? null,
      speed: s,
      direction: d,
      gust: hourly?.wind_gusts_10m?.[i] ?? null,
      temperature: hourly?.temperature_2m?.[i] ?? null,
      apparentTemperature: hourly?.apparent_temperature?.[i] ?? null,
      precipitationProbability: hourly?.precipitation_probability?.[i] ?? null,
      weatherCode: hourly?.weather_code?.[i] ?? null,
      humidity: hourly?.relative_humidity_2m?.[i] ?? null,
      precipitation: hourly?.precipitation?.[i] ?? null,
      cloudCover: hourly?.cloud_cover?.[i] ?? null,
      pressureMsl: hourly?.pressure_msl?.[i] ?? null,
      swellHeight: hourly?.swell_wave_height?.[i] ?? null,
      swellDirection: hourly?.swell_wave_direction?.[i] ?? null,
      swellPeriod: hourly?.swell_wave_period?.[i] ?? null,
    };
  });
  const lat = keepReturnedCoords ? (entry?.latitude ?? cell.lat) : cell.lat;
  const lon = keepReturnedCoords ? (entry?.longitude ?? cell.lon) : cell.lon;
  return { lat, lon, hours };
}

/** Assembled viewport cell-set result (network + cache provenance). */
interface ResolvedCells {
  cells: ForecastCell[];
  points: WindFieldPointForecast[];
  fromCache: boolean;
  fallback: boolean;
  cacheAgeMs: number;
  requestedCellCount: number;
  networkCellCount: number;
}

/**
 * Assemble a viewport cell set for one data kind (v56 Task 3 fetch order):
 *
 *   1. memory records first (per cell, kind-specific freshness);
 *   2. then persistent records for the cells memory could not serve;
 *   3. return immediately when every cell is fresh;
 *   4. otherwise put only the missing / not-fresh coordinates into ONE
 *      multi-location provider request (normalized in request order);
 *   5. save each returned cell separately (memory + persistent);
 *   6. assemble the result in the planner's original cell order;
 *   7. on network failure, serve stale-but-usable records for exactly
 *      those same cells — never substitute cells from a different area.
 *
 * A requested cell with no usable saved record rethrows the network error
 * (a viewport result must cover every requested cell or fail as a whole).
 */
async function resolveForecastCells(
  kind: ForecastDataType,
  uniqueCells: ForecastCell[],
  hourWindow: HourWindow,
  dayAnchor: number,
  signal: AbortSignal,
): Promise<ResolvedCells> {
  // Lazy: the cache module loads db.ts → authStore → pocketbase (window).
  const cache = await import('./forecastCache');
  const selection = forecastCellSelectionFor(kind);
  const keyOf = (cell: ForecastCell) =>
    cache.forecastCellCacheKey(kind, cell.lat, cell.lon, dayAnchor, selection);
  const now = Date.now();

  // 1+2. Memory first, then persistent storage — best record per cell.
  const records = new Map<string, ForecastCacheRecord>();
  const persistentKeys: string[] = [];
  for (const cell of uniqueCells) {
    const k = keyOf(cell);
    const mem = _cellCache.get(k);
    if (mem) records.set(k, mem);
    else persistentKeys.push(k);
  }
  if (persistentKeys.length > 0) {
    const stored = await cache.getForecastCacheRecords(persistentKeys);
    for (const [k, rec] of stored) {
      if (!records.has(k)) {
        records.set(k, rec);
        _cellCache.set(k, rec); // promote to memory for the next call
      }
    }
  }

  // 3+4. Fresh cells are served; missing / stale / expired cells are
  // requested once per kind. Stale-but-usable records (within 24h) are
  // kept as the same-cell fallback for a network failure.
  const missing: ForecastCell[] = [];
  const fallbackFor = new Map<string, ForecastCacheRecord>();
  for (const cell of uniqueCells) {
    const k = keyOf(cell);
    const rec = records.get(k);
    if (!rec) {
      missing.push(cell);
      continue;
    }
    const status = cache.forecastCacheStatus(rec, now);
    if (status === 'fresh') continue;
    missing.push(cell);
    if (status === 'stale') fallbackFor.set(k, rec);
  }

  if (missing.length === 0) {
    // 3. All fresh — no network, no fallback label.
    return {
      cells: uniqueCells,
      points: uniqueCells.map((cell) => records.get(keyOf(cell))!.value),
      fromCache: true,
      fallback: false,
      cacheAgeMs: 0,
      requestedCellCount: uniqueCells.length,
      networkCellCount: 0,
    };
  }

  // 5. One provider request carrying ONLY the missing / not-fresh cells.
  const url = buildForecastCellsUrlForCells(kind, missing, hourWindow);
  let data: unknown;
  try {
    data = await fetchJson(url, signal);
  } catch (e) {
    if (isAbortError(e) || signal.aborted) throw abortError();
    // 7. Same-cell fallback: stale-but-usable records for exactly the
    // cells that were being refreshed. Any requested cell without a
    // usable record means this viewport cannot be served — rethrow
    // (data from a different map area must never be moved onto a new
    // coast).
    let maxFallbackAge = 0;
    const points: WindFieldPointForecast[] = [];
    for (const cell of uniqueCells) {
      const k = keyOf(cell);
      const rec = records.get(k);
      if (rec && cache.forecastCacheStatus(rec, now) === 'fresh') {
        points.push(rec.value);
        continue;
      }
      const fb = fallbackFor.get(k);
      if (!fb) throw e;
      maxFallbackAge = Math.max(maxFallbackAge, now - fb.fetchedAt);
      points.push(fb.value);
    }
    return {
      cells: uniqueCells,
      points,
      fromCache: true,
      fallback: true,
      cacheAgeMs: maxFallbackAge,
      requestedCellCount: uniqueCells.length,
      networkCellCount: 0,
    };
  }

  const entries = (Array.isArray(data) ? data : [data]) as OpenMeteoEntry[];
  // Normalize in REQUEST order; swell/weather keep the returned model
  // cell coordinates (sea selection can resolve to an offshore cell).
  const networkPoints = missing.map((cell, i) =>
    normalizeCellForecast(entries[i], cell, kind !== 'pressure'),
  );

  // 6. Save each returned cell separately — memory now, persistent later.
  const fetchedAt = Date.now();
  const newRecords: ForecastCacheRecord[] = missing.map((cell, i) => ({
    key: keyOf(cell),
    kind,
    requestLat: cell.lat,
    requestLon: cell.lon,
    fetchedAt,
    expiresAt: cache.forecastExpiry(kind, fetchedAt),
    usableUntil: cache.forecastUsableUntil(fetchedAt),
    value: networkPoints[i],
  }));
  for (const rec of newRecords) _cellCache.set(rec.key, rec);
  void cache.putForecastCacheRecords(newRecords); // fail-open; never blocks the render

  // 7. Assemble the result in the planner's original cell order: fresh
  // records keep their stored values, the rest take the network values.
  const points: WindFieldPointForecast[] = [];
  let ni = 0;
  for (const cell of uniqueCells) {
    const k = keyOf(cell);
    const rec = records.get(k);
    if (rec && cache.forecastCacheStatus(rec, now) === 'fresh') points.push(rec.value);
    else points.push(networkPoints[ni++]);
  }

  return {
    cells: uniqueCells,
    points,
    fromCache: false,
    fallback: false,
    cacheAgeMs: 0,
    requestedCellCount: uniqueCells.length,
    networkCellCount: missing.length,
  };
}

/**
 * Fetch (or serve from cache) the bounded REAL forecast cells covering a
 * viewport for one data kind.
 *
 *   - planForecastCells / planViewportSampleCells + dedupeCells: one
 *     planned cell set per request, capped at 25 cells in the
 *     'viewport-summary' mode
 *   - per-cell memory cache first, then the device-wide persistent cache,
 *     then ONE network request containing only missing / not-fresh cells
 *   - in-flight key: `{kind}:cells:{spacing}:{bbox}:{hourWindow}:
 *     {selection}:best_match` — identical requests coalesce onto one fetch
 *   - NO cross-key supersede: the Conditions store and the weather/swell
 *     marker layers all fetch the same kinds for (nearly) the same viewport,
 *     so a newer request must never abort an in-flight fetch another caller
 *     is waiting on. Each caller's result stays fresh for its own bounds.
 *   - a network failure serves stale-but-usable records for the SAME
 *     requested cells (marked `fallback` with `cacheAgeMs`); cells that
 *     were never saved anywhere cause the request to fail instead of
 *     borrowing data from a different area.
 */
export async function fetchForecastCells(
  request: ForecastCellsRequest,
): Promise<ForecastCellsResult> {
  const kind = request.kind;
  const sampling = request.sampling ?? 'field';
  const plan = planCellsForSampling(request.bounds, sampling, request.maxPoints);
  const uniqueCells = dedupeCells(plan.cells);
  const hourWindow = request.hourWindow ?? { start: 0, end: FORECAST_HOURS };
  const key = forecastCellsCacheKey(kind, plan.geometry, hourWindow);
  const source = forecastSourceFor(kind);
  const dayAnchor = forecastDayAnchor(hourWindow);

  const inflight = _inflight.get(key);
  if (inflight) return inflight.promise as Promise<ForecastCellsResult>;

  const controller = new AbortController();
  _cellControllers.set(key, controller);

  const promise: Promise<ForecastCellsResult> = (async (): Promise<ForecastCellsResult> => {
    try {
      const resolved = await resolveForecastCells(
        kind,
        uniqueCells,
        hourWindow,
        dayAnchor,
        controller.signal,
      );
      const sourceTime = resolved.points[0]?.hours[0]?.time ?? '';
      return {
        ...resolved,
        sourceTime,
        forecastTime: request.forecastTime ?? sourceTime,
        source,
        kind,
        stale: false,
      };
    } catch (e) {
      if (isAbortError(e)) {
        // Aborted (e.g. cache reset) — never cache, never throw.
        return {
          cells: uniqueCells,
          points: [],
          sourceTime: '',
          forecastTime: request.forecastTime ?? '',
          source,
          kind,
          stale: true,
          fromCache: false,
          cacheAgeMs: 0,
          fallback: false,
          requestedCellCount: uniqueCells.length,
          networkCellCount: 0,
        };
      }
      throw e;
    } finally {
      // Only one in-flight entry per key (coalescing), so this entry is
      // ours unless a reset cleared the map and a newer fetch took the key.
      if (_cellControllers.get(key) === controller) {
        _inflight.delete(key);
        _cellControllers.delete(key);
      }
    }
  })();

  _inflight.set(key, { epoch: 0, promise });
  return promise;
}
